import React from "react";
import { calculateEarthing } from "@multiconsult-group/multiplacer-core";
import { Box, Button, Code, Group, Modal, ScrollArea, Stack, Text, Alert } from "@mantine/core";
import { usePostEarthingCalculateMutation } from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setProjectViewerEarthingCalculationResult } from "../projectViewerSlice";
import { selectProjectViewerEarthingCalculationInput } from "./jordningSelectors";

type IJordningGeneratorModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const JordningGeneratorModal: React.FC<IJordningGeneratorModalProps> = ({
  opened,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const earthingCalculationInput = useAppSelector(selectProjectViewerEarthingCalculationInput);
  const [postEarthingCalculate, postEarthingCalculateState] = usePostEarthingCalculateMutation();
  const [apiResponse, setApiResponse] = React.useState<unknown>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleGenerateEarthing = () => {
    if (earthingCalculationInput == null) return;

    try {
      setError(null);
      setApiResponse(null);
      const result = calculateEarthing(earthingCalculationInput);
      dispatch(setProjectViewerEarthingCalculationResult(result));
      onClose();
    } catch (err) {
      console.error("[Jordning] calculateEarthing failed", err);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSendEarthingToApi = async () => {
    if (earthingCalculationInput == null) return;

    try {
      setError(null);
      setApiResponse(null);
      const result = await postEarthingCalculate({
        body: earthingCalculationInput,
      }).unwrap();
      setApiResponse(result);
      dispatch(setProjectViewerEarthingCalculationResult(result));
    } catch (err) {
      console.error("[Jordning] API calculate earthing failed", err);
      setApiResponse(err);
      setError(err instanceof Error ? err.message : JSON.stringify(err, null, 2));
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Generer jordning" size="lg" centered>
      <Stack gap="md">
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Beregningsinput
          </Text>
          {earthingCalculationInput == null ? (
            <Text size="sm" c="dimmed">
              Ingen apparatfundament funnet i valgte etasjer.
            </Text>
          ) : (
            <Box
              p="md"
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 4,
                background: "var(--mantine-color-gray-0)",
              }}
            >
              <ScrollArea.Autosize mah={520} type="auto" offsetScrollbars>
                <Code block>{JSON.stringify(earthingCalculationInput, null, 2)}</Code>
              </ScrollArea.Autosize>
            </Box>
          )}
        </Stack>

        {apiResponse != null && (
          <Stack gap="xs">
            <Text fw={600} size="sm">
              API-svar
            </Text>
            <Box
              p="md"
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 4,
                background: "var(--mantine-color-gray-0)",
              }}
            >
              <ScrollArea.Autosize mah={360} type="auto" offsetScrollbars>
                <Code block>{JSON.stringify(apiResponse, null, 2)}</Code>
              </ScrollArea.Autosize>
            </Box>
          </Stack>
        )}

        {error != null && (
          <Alert color="red" title="Beregning feilet" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onClose}>
            Avbryt
          </Button>
          <Button
            variant="outline"
            disabled={earthingCalculationInput == null}
            loading={postEarthingCalculateState.isLoading}
            onClick={handleSendEarthingToApi}
          >
            Send til API
          </Button>
          <Button disabled={earthingCalculationInput == null} onClick={handleGenerateEarthing}>
            Generer jordning
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
